import type { JsonValue } from "common/types/json";

/**
 * Needs to be kept in sync with DB enum values.
 */
export const builderCategoryValues = ["CUSTOMER", "DEMO", "PROSPECT", "TESTING", "TRAINING", "LEGACY"] as const;
export type BuilderCategory = (typeof builderCategoryValues)[number];

export interface Builder {
  id: number;
  category: BuilderCategory;
  companyName: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  website: string | null;
  logoUrl: string | null;
  creditReloadThreshold: number | null;
  creditReloadAmount: number | null;
  salesConfig: JsonValue;
  brochureSettings: JsonValue;
  pricingVisibilitySettings: JsonValue;
  enabledProducts: JsonValue;
  featureFlags: JsonValue;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Needs to be kept in sync with DB enum values.
 */
export const builderBrochureImageOptionValues = ["3d-line", "3d-view", "image"] as const;
export type BuilderBrochureImageOption = (typeof builderBrochureImageOptionValues)[number];

/**
 * Needs to be kept in sync with DB enum values.
 */
export const builderProductValues = ["api", "procure", "showroom", "studio", "autoRenders"] as const;
export type BuilderProduct = (typeof builderProductValues)[number];
export type BuilderProducts = Partial<Record<BuilderProduct, boolean>>;

export const DEFAULT_ENABLED_PRODUCTS: BuilderProducts = {
  studio: true,
  procure: true,
  showroom: true,
} as const;

export type BuilderBrochureSettings = {
  readonly imageType?: BuilderBrochureImageOption;
  readonly displayOptionPrices?: boolean;
  readonly displayOptionDescriptions?: boolean;
  readonly displayAllOptions?: boolean;
  readonly displayFairHousingLogo?: boolean;
  readonly displayElevationPage?: boolean;
  readonly displayDefaultExteriorPaletteWhenSelected?: boolean;
  readonly displaySqft?: boolean;
  readonly displayPlanLabelDims?: boolean;
  readonly displayElectricalOptions?: boolean;
  readonly legalDisclaimer?: string;
  readonly website?: string;
  readonly logoUrl?: string;
  readonly singlePlanPerPage?: boolean;
  readonly addElectricalPlans?: boolean;
};

export type BuilderPricingVisibilitySettings = {
  readonly communityPlans?: boolean;
  readonly internalHomePlanLinks?: boolean;
  readonly publicHomePlanLinks?: boolean;
  readonly optionSummary?: boolean;
  readonly salesBrochure?: boolean;
  readonly publicSalesMap?: boolean;
};

type LocationSettingValue = {
  readonly enabled: boolean;
  readonly displayName?: string;
  readonly examples?: string;
  readonly icon?: string;
};

export type BuilderLocationSettings = {
  readonly company?: LocationSettingValue;
  readonly region?: LocationSettingValue;
  readonly division?: LocationSettingValue;
  readonly community?: LocationSettingValue;
  readonly phase?: LocationSettingValue;
  readonly multiunit?: LocationSettingValue;
};

export const LOCATION_TYPES_ORDER = ["company", "region", "division", "community", "phase", "multiunit"] as const;
export type LocationType = (typeof LOCATION_TYPES_ORDER)[number];

/**
 * Default location settings for new builders
 * - Company and region are disabled by default (opt-in feature)
 * - Division, community, and phase are enabled by default (existing functionality)
 */
export const DEFAULT_LOCATION_SETTINGS: BuilderLocationSettings = {
  company: { enabled: false },
  region: { enabled: false },
  division: { enabled: true },
  community: { enabled: true },
  phase: { enabled: true },
  multiunit: { enabled: false },
} as const;

export const LOCATION_SETTINGS_CONFIG: Record<
  LocationType,
  {
    label: string;
    example: string;
    defaultIcon: string;
  }
> = {
  company: {
    label: "Company",
    example: "Ex: South, Midwest, West",
    defaultIcon: "company",
  },
  region: {
    label: "Region",
    example: "Ex: North Carolina, Georgia, Texas",
    defaultIcon: "region",
  },
  division: {
    label: "Division",
    example: "Ex: Charlotte, Durham, Raleigh",
    defaultIcon: "division",
  },
  community: {
    label: "Community",
    example: "Ex: Sun Valley, Brookhaven, Highland Acres",
    defaultIcon: "community",
  },
  phase: {
    label: "Phase",
    example: "Ex: Phase 1, Phase 2, Phase 3",
    defaultIcon: "phase",
  },
  multiunit: {
    label: "Multi-Unit",
    example: "Ex: Townhome building name or address",
    defaultIcon: "buildingApartment",
  },
};

// Icon options for location types, ordered to match the design mock
export const AVAILABLE_LOCATION_ICONS = [
  "globeHemisphereWest",
  "globeSimple",
  "compass",
  "region",
  "mapPinSimpleArea",
  "mapPinSimple",
  "mapPinArea",
  "briefcaseSimple",
  "company",
  "division",
  "buildingApartment",
  "community",
  "buildings",
  "warehouse",
  "phase",
  "storefront",
  "truck",
  "ladder",
  "barricade",
  "paintBrushHousehold",
  "pipeWrench",
  "hardHat",
  "crane",
  "tractor",
  "bulldozer",
  "network",
  "usersFour",
  "mountains",
] as const;

export type LocationIconName = (typeof AVAILABLE_LOCATION_ICONS)[number];

/**
 * Determines if allowHomePlans should default to true for a given location level.
 * Home plans are allowed by default on communities and phases.
 */
export function shouldAllowHomePlansByDefault(locationLevel: string | null | undefined): boolean {
  const level = locationLevel?.toLowerCase();
  return level === "community" || level === "phase" || level === "multiunit";
}
